import CVS from 'assets/images/depositCash/cvs.svg';
import Kroger from 'assets/images/depositCash/kroger.svg';
import SevenEleven from 'assets/images/depositCash/sevenEleven.svg';
import Walgreens from 'assets/images/depositCash/walgreens.svg';
import Walmart from 'assets/images/depositCash/walmart.svg';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import ExpressiveXLText from 'shared/components/vxl/typography/expressive/ExpressiveXLText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkXsText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkXSText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import useIsFeatureEnabled from 'shared/hooks/statsig/useIsFeatureEnabled/useIsFeatureEnabled';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { openCVSLink } from 'shared/utils/depositCash';
import {
  showZendeskArticle,
  ZendeskArticles,
} from 'shared/utils/zendesk/zendeskUtils';

function DepositCashScreen() {
  const { segmentClient } = useAnalyticsConfiguration();
  const { bottom } = useSafeAreaInsets();
  const isKrogerBarcodeEnabled = useIsFeatureEnabled(
    FeatureFlagKey.ENABLE_GD_BARCODE_KROGER,
  );

  const router = useRouter();
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'depositCash',
  });

  const cvsPrimaryCopyKey = isKrogerBarcodeEnabled
    ? 'cvsPrimaryCopyKroger'
    : 'cvsPrimaryCopyExtended';

  const disclaimerTextPrefix = isKrogerBarcodeEnabled
    ? 'disclaimerTextKroger'
    : 'disclaimerTextExtended';

  const imageSources = [
    {
      id: 'cvs',
      source: <CVS />,
      label: 'CVS illustration',
      style: styles.locations,
    },
    {
      id: '7-eleven',
      source: <SevenEleven />,
      label: '7-Eleven illustration',
      style: styles.imageEven,
    },
    isKrogerBarcodeEnabled
      ? {
          id: 'kroger',
          source: <Kroger />,
          label: 'Kroger illustration',
          style: styles.imageKroger,
        }
      : {
          id: 'walgreens',
          source: <Walgreens />,
          label: 'Walgreens illustration',
          style: styles.imageWalgreens,
        },
    isKrogerBarcodeEnabled
      ? {
          id: 'walgreens',
          source: <Walgreens />,
          label: 'Walgreens illustration',
          style: styles.imageWalgreens,
        }
      : {
          id: 'walmart',
          source: <Walmart />,
          label: 'Walmart illustration',
          style: styles.imageWalmart,
        },
  ];

  return (
    <>
      <Stack.Screen
        options={{
          title: translatedText('header'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              trailingAccessories={
                <Button
                  label={translatedText('howToDepositFaq')}
                  type="TEXT"
                  onPress={() => {
                    showZendeskArticle(ZendeskArticles?.DEPOSIT_CASH);
                    void segmentClient.logScreenEvent('Deposit Cash FAQ', {
                      name: 'Deposit Cash FAQ',
                      category: '',
                    });
                  }}
                  analyticsId="depositCashFaq"
                  customAttributes={{ faq_name: 'cash_deposit' }}
                />
              }
            />
          ),
        }}
      />
      <View style={styles.bodyContentContainer}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollViewContentContainer}
        >
          <View style={styles.primaryContentInner}>
            <ExpressiveXLText numberOfLines={0} style={styles.title}>
              {translatedText('title')}
            </ExpressiveXLText>
            <BodyBaseText numberOfLines={0} style={styles.subtitle}>
              {translatedText('subtitle')}
            </BodyBaseText>

            <View style={styles.listGroup}>
              <View style={styles.listItemFlush}>
                <ListItem
                  primaryCopy={translatedText(cvsPrimaryCopyKey)}
                  secondaryCopy={translatedText('cvsSecondaryCopyExtended')}
                  showsChevron={false}
                  leadingAccessory={{
                    id: 'icon',
                    name: VXLComponentId.ICON_AVATAR,
                    props: { variant: 'MD', iconKey: 'NO_FEES' },
                  }}
                />
              </View>

              <HairLineDivider />

              <View style={styles.listItemFlush}>
                <ListItem
                  primaryCopy={translatedText('feePrimaryCopy')}
                  secondaryCopy={translatedText('feeSecondaryCopy')}
                  showsChevron={false}
                  leadingAccessory={{
                    id: 'icon',
                    name: VXLComponentId.ICON_AVATAR,
                    props: { variant: 'MD', iconKey: 'MONEY' },
                  }}
                />
              </View>

              <HairLineDivider />
            </View>

            <View style={styles.imageContainer}>
              {imageSources.map((image) => (
                <View
                  style={image.style}
                  key={image.id}
                  accessibilityLabel={image.label}
                >
                  {image.source}
                </View>
              ))}
            </View>

            <View style={styles.footerText}>
              <BodyXSText numberOfLines={0} style={styles.disclaimerTextColor}>
                {translatedText(`${disclaimerTextPrefix}.footnoteAvailability`)}
              </BodyXSText>
              <BodyXSText
                numberOfLines={0}
                style={[styles.disclaimerTextColor, styles.disclaimerParagraph]}
              >
                {translatedText(`${disclaimerTextPrefix}.footnoteCVS`)}
                <HyperlinkXsText onPress={openCVSLink}>
                  {translatedText(`${disclaimerTextPrefix}.url`)}
                </HyperlinkXsText>
              </BodyXSText>
              <BodyXSText
                numberOfLines={0}
                style={[styles.disclaimerTextColor, styles.disclaimerParagraph]}
              >
                {translatedText(`${disclaimerTextPrefix}.footnoteBrands`)}
              </BodyXSText>
              <BodyXSText
                numberOfLines={0}
                style={[styles.disclaimerTextColor, styles.disclaimerParagraph]}
              >
                {translatedText(`${disclaimerTextPrefix}.footnoteGreenDot`)}
              </BodyXSText>
            </View>
          </View>
        </ScrollView>
        <View
          style={[
            styles.buttonContainer,
            { marginBottom: bottom || theme.spacing.m },
          ]}
        >
          <Button
            label={translatedText('showDepositLocationsButton')}
            onPress={() => router.push('/move-money/deposit-cash/map')}
            analyticsId="showDepositLocations"
          />
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  primaryContentInner: {
    paddingTop: theme.spacing.xl,
    paddingHorizontal: theme.spacing.m,
    gap: theme.spacing.m,
    flex: 1,
  },

  listGroup: {
    alignSelf: 'stretch',
    marginHorizontal: 0,
    marginTop: 0,
  },
  bodyContentContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  buttonContainer: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.border.primarySolid,
    borderTopWidth: 1,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  disclaimerTextColor: {
    color: theme.color.text.secondary,
  },

  disclaimerParagraph: {
    marginTop: theme.spacing.s,
  },

  footerText: {
    paddingVertical: theme.spacing.s,
  },

  listItemFlush: {
    marginHorizontal: -theme.spacing.m,
  },

  imageContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: theme.spacing.m,
  },
  imageEven: {
    height: theme.spacing.l,
    width: 18,
  },
  imageWalgreens: {
    height: theme.spacing.l,
    width: 77,
  },
  imageKroger: {
    width: 64,
    height: theme.spacing.l,
  },
  imageWalmart: {
    width: 72,
    height: theme.spacing.l,
  },
  locations: {
    height: theme.spacing.l,
    width: 66,
  },
  scrollView: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollViewContentContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  subtitle: {
    color: theme.color.text.primary,
    textAlign: 'center',
  },
  title: {
    textAlign: 'center',
    textTransform: 'uppercase',
  },
});

export default DepositCashScreen;
